/**
 * Electrical current formatting utilities (flow of electric charge)
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { CurrentFormat, FieldValue, FormattedValue } from "./types";

/**
 * Format as milliamps
 */
export const toMilliamps = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  const mA = value * 1000;
  return decimals === 0 ? `${Math.round(mA)} mA` : `${mA.toFixed(decimals)} mA`;
};

/**
 * Format as amperes
 */
export const toAmps = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} A`;
};

/**
 * Format as kiloamps (rarely used but included for completeness)
 */
export const toKiloamps = (value: number | null | undefined, decimals: number = 2): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kA`;
};

/**
 * Auto-scale current to the most appropriate unit
 */
export const autoScaleCurrent = (value: number | null | undefined, decimals?: number): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (absValue >= 1000) {
    return toKiloamps(value, decimals ?? 2);
  } else if (absValue < 1) {
    return toMilliamps(value, decimals ?? 0);
  } else {
    return toAmps(value, decimals ?? 1);
  }
};

/**
 * Format current according to CurrentFormat specification
 */
export const formatCurrent = (value: FieldValue, format: CurrentFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "kA":
        return toKiloamps(numValue, decimals ?? 2);
      case "mA":
        return toMilliamps(numValue, decimals ?? 0);
      case "A":
        return toAmps(numValue, decimals ?? 1);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScaleCurrent(numValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base amps
  return toAmps(numValue, decimals ?? 1);
};

// Register the formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("current", formatCurrent as any);

// Convenience exports with shorter names
export const toMA = toMilliamps;
export const toA = toAmps;
export const toKA = toKiloamps;
